import { ReactNode, Suspense } from "react";
import { ButtonBS, Center, Grid, Heading, Stack } from "@biblioteksentralen/react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { VisuallyHiddenResults } from "../VisuallyHiddenResults";
import BookListPreview from "../lists/BookListPreview";
import RecommendationCard from "../recommendation/RecommendationCard";
import { useUrlState } from "../../utils/hooks/useUrlState";
import { ResolvedEmployee, ResolvedListSummary, ResolvedRecommendationSummary } from "@libry-content/common";

type Props = {
  employee: ResolvedEmployee;
  listsAndRecommendations: (ResolvedListSummary | ResolvedRecommendationSummary)[];
  total: number;
};

const pageSize = 9;
const maxTotalNumber = Number.POSITIVE_INFINITY;

export const EmployeeViewContentWrapper = ({ listsAndRecommendations, total, employee }: Props) => {
  if (!listsAndRecommendations.length) return null;

  return (
    <Suspense
      fallback={
        <EmployeeViewContent employee={employee} listsAndRecommendations={listsAndRecommendations} total={total} />
      }
    >
      <EmployeeViewContentWithPagination
        employee={employee}
        listsAndRecommendations={listsAndRecommendations}
        total={total}
      />
    </Suspense>
  );
};

const EmployeeViewContent = (props: {
  employee: ResolvedEmployee;
  listsAndRecommendations: (ResolvedListSummary | ResolvedRecommendationSummary)[];
  total: number;
  numberOfItemsToShow?: number;
  showMoreButton?: ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Stack>
      <Heading fontSize="2xl">
        {t("{name} anbefaler ({total})", { name: props.employee.name?.split(" ")?.slice(0, 1)[0], total: props.total })}
      </Heading>
      {props.numberOfItemsToShow && (
        <VisuallyHiddenResults
          itemsLength={props.numberOfItemsToShow}
          name={props.numberOfItemsToShow > 1 ? t("anbefalinger") : t("anbefaling")}
        />
      )}
      <Grid gap="1rem 1rem" gridTemplateColumns="repeat(auto-fill, minmax(13em, 1fr))">
        {props.listsAndRecommendations?.map((item) =>
          item._type === "recommendation" ? (
            <RecommendationCard key={item._id} recommendation={item} />
          ) : (
            <BookListPreview key={item._id} list={item} />
          )
        )}
      </Grid>
      {props.showMoreButton}
    </Stack>
  );
};

const EmployeeViewContentWithPagination = ({ listsAndRecommendations, total, employee }: Props) => {
  const { t } = useTranslation();
  const [numberOfListsAndRecommendations, setNumberOfListsAndRecommendations] = useUrlState<number>(
    Math.min(pageSize, maxTotalNumber),
    "size"
  );

  const shouldShowMoreButton =
    total > numberOfListsAndRecommendations && numberOfListsAndRecommendations < maxTotalNumber;

  const handleShowMore = () => {
    setNumberOfListsAndRecommendations(Math.min(numberOfListsAndRecommendations + pageSize, maxTotalNumber));
  };

  const listsAndRecommendationsToShow = listsAndRecommendations.slice(0, numberOfListsAndRecommendations);
  const numberOfItemsToShow = listsAndRecommendationsToShow.length;

  const showMoreButton = (
    <>
      {shouldShowMoreButton && (
        <Center marginTop="2rem">
          <ButtonBS variant="secondary" onClick={handleShowMore}>
            {t("Vis mer")}
          </ButtonBS>
        </Center>
      )}
    </>
  );

  return (
    <EmployeeViewContent
      listsAndRecommendations={listsAndRecommendationsToShow}
      total={total}
      employee={employee}
      showMoreButton={showMoreButton}
      numberOfItemsToShow={numberOfItemsToShow}
    />
  );
};
